'use client'
import { type PropsWithChildren, createContext, useContext } from 'react';

import { useAccount } from 'reservations/account/providers/AccountProvider';
import { useConfigQuery } from 'reservations/account/queries/useConfigQuery';
import { type Config } from 'reservations/account/types';

const ConfigContext = createContext<Config | null>(null);

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const { data, isError, isPending } = useConfigQuery();

  if (isPending) {
    return <></>;
  }

  // TODO: Figure out how to handle error state here in the future
  // istanbul ignore next
  if (isError) {
    console.error('Failed to load Nextable Config');
    return null;
  }
  return (
    <ConfigContext.Provider value={data}>{children}</ConfigContext.Provider>
  );
};

export const useBookingConfig = () => useConfig()?.booking;

export const useAcceptsReservations = () => useBookingConfig()?.acceptsParties;

export const useIsWaitlistOnly = () => !useBookingConfig()?.featureEnabled;

export const useAcceptsWaitlist = () => useConfig()?.waitlist?.acceptsParties;

export const useFeePolicy = () => useConfig()?.feePolicy;

export const useLocation = () => useConfig()?.location;

export const useIsChecklessPaymentsEnabled = () =>
  useConfig()?.settings.is_checkless_payments_enabled;

export const useWaitlistClosedText = () =>
  useConfig()?.settings.waitlist_closed_text;

export const useLocationId = () => useLocation().id;

export const usePhoneNumber = () => useLocation().phone_number;

export const useEmailAddress = () => useLocation().email_address;

export const useRestaurantName = () => {
  const {
    connected_location_integrations: connectedLocationIntegrations,
    restaurantName
  } = useAccount();
  const { name: locationName } = useLocation();
  const isMultiLocation = connectedLocationIntegrations.length > 1;
  const name = isMultiLocation
    ? `${restaurantName} - ${locationName}`
    : restaurantName;

  return name;
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context == null) {
    throw new Error('useConfig must be used within ConfigProvider');
  }

  return context;
};
